import React, { useState, useCallback } from 'react'
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Paper,
    Chip,
    TextField,
    InputAdornment,
    makeStyles,
    useTheme,
    CircularProgress,
    Card,
    CardContent,
    CardHeader,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import { Search, Warning, Person, Send } from '@material-ui/icons'
import { useRent } from '../../hooks/useRent'
import { toast } from 'react-toastify'

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(1),
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    table: {
        minWidth: 650,
        '& .MuiTableCell-head': {
            backgroundColor: theme.palette.grey[50],
            fontWeight: 600,
            fontSize: '0.875rem',
        },
        '& .MuiTableRow-root:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    amountCell: {
        fontWeight: 600,
        color: theme.palette.error.main,
    },
    daysLateCell: {
        fontWeight: 600,
    },
    actionButtons: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(4),
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary,
    },
    unitChip: {
        fontWeight: 500,
    },
    areaChip: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        fontWeight: 600,
        fontSize: '0.875rem',
        padding: theme.spacing(0.5, 1),
    },
    searchBox: {
        display: 'flex',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    tableContainer: {
        flex: 1,
        position: 'relative',
        marginBottom: theme.spacing(8),
        overflow: 'hidden',
    },
    paginationContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(2),
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        minHeight: '80px',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10,
    },
    tenantCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
}))

export const DelinquencyTab: React.FC = () => {
    const classes = useStyles()
    const theme = useTheme()
    const [page, setPage] = useState(1)
    const [pageSize] = useState(50)

    const { useDelinquentAccounts, sendOverdueNotice } = useRent()
    const { data, isLoading, error } = useDelinquentAccounts()
    const [sendingReminder, setSendingReminder] = useState<number | null>(null)

    const handleSendReminder = async (tenantId: number) => {
        try {
            setSendingReminder(tenantId)
            await sendOverdueNotice({ tenant_id: tenantId })
            toast.success('Reminder sent successfully')
        } catch (error: any) {
            console.error('Error sending reminder:', error)
            const errorMessage =
                error.response?.data?.message || 'Failed to send reminder'
            toast.error(errorMessage)
        } finally {
            setSendingReminder(null)
        }
    }

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount)
    }

    const formatDate = useCallback((dateString: string | null) => {
        if (!dateString) return 'Never'
        // Split YYYY-MM-DD format and create date parts
        const [year, month, day] = dateString.split('-').map(Number)
        // Month is 0-based in JS Date
        const date = new Date(year, month - 1, day)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    }, [])

    const getDaysLateColor = (days: number) => {
        if (days <= 7) return 'primary' // Less severe - just a few days late
        if (days <= 30) return 'secondary' // More severe - up to a month late
        return 'secondary' // Most severe - over a month late
    }

    const getDaysLateStyle = (days: number) => {
        if (days <= 7) return {} // Default style for primary
        if (days <= 30) return {} // Default style for secondary
        return {
            // Custom style for most severe
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
        }
    }

    const accounts = data || []
    const totalCount = accounts.length
    const totalPages = Math.ceil(totalCount / pageSize)
    const startIndex = (page - 1) * pageSize
    const endIndex = startIndex + pageSize
    const paginatedAccounts = accounts.slice(startIndex, endIndex)

    const handlePageChange = (
        event: React.ChangeEvent<unknown>,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    if (isLoading) {
        return (
            <Card style={{ marginTop: 0 }}>
                <CardContent>
                    <Box className={classes.loadingContainer}>
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        )
    }

    if (error) {
        return (
            <Card style={{ marginTop: 0 }}>
                <CardContent>
                    <Box className={classes.emptyState}>
                        <Warning
                            style={{
                                fontSize: 48,
                                marginBottom: 16,
                                color: theme.palette.error.main,
                            }}
                        />
                        <Typography variant="h6" style={{ marginBottom: 8 }}>
                            Error Loading Delinquent Accounts
                        </Typography>
                        <Typography variant="body2">
                            {error.message ||
                                'Failed to load delinquent accounts'}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        )
    }

    return (
        <Box className={classes.container}>
            {/* Header */}
            <Box style={{ marginBottom: theme.spacing(2), flexShrink: 0 }}>
                <Typography variant="h5" component="h2" gutterBottom>
                    Delinquent Accounts ({totalCount})
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Track and manage overdue tenant accounts
                </Typography>
            </Box>

            {/* Table */}
            <Paper className={classes.tableContainer}>
                {paginatedAccounts.length > 0 ? (
                    <TableContainer
                        style={{
                            height: 'calc(100% - 10px)',
                            overflow: 'auto',
                        }}
                    >
                        <Table
                            stickyHeader
                            className={classes.table}
                            style={{ minWidth: 1500 }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>Tenant</TableCell>
                                    <TableCell>Unit Name</TableCell>
                                    <TableCell>Area</TableCell>
                                    <TableCell>Days Late</TableCell>
                                    <TableCell>Amount Owed</TableCell>
                                    <TableCell>Last Payment Date</TableCell>
                                    <TableCell>Last Payment Amount</TableCell>
                                    <TableCell align="center">
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedAccounts.map((account) => (
                                    <TableRow key={account.id}>
                                        <TableCell>
                                            <Box className={classes.tenantCell}>
                                                <Person fontSize="small" />
                                                <Typography
                                                    variant="body2"
                                                    style={{ fontWeight: 500 }}
                                                >
                                                    {account.tenant.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={
                                                    account.area?.unit?.name ||
                                                    'N/A'
                                                }
                                                variant="outlined"
                                                size="small"
                                                className={classes.unitChip}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {account.area?.area_config.name}{' '}
                                                -{' '}
                                                {account.area?.area_label ||
                                                    'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={`${account.days_behind} days`}
                                                color={getDaysLateColor(
                                                    account.days_behind,
                                                )}
                                                style={getDaysLateStyle(
                                                    account.days_behind,
                                                )}
                                                size="small"
                                                className={classes.daysLateCell}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                className={classes.amountCell}
                                            >
                                                {formatCurrency(
                                                    account.total_amount_owed,
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(
                                                    account.last_payment_date,
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {account.last_payment_amount
                                                    ? formatCurrency(
                                                          account.last_payment_amount,
                                                      )
                                                    : 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                icon={<Send fontSize="small" />}
                                                label={
                                                    sendingReminder ===
                                                    account.tenant.id
                                                        ? 'Sending...'
                                                        : 'Send Reminder'
                                                }
                                                size="small"
                                                variant="outlined"
                                                clickable
                                                onClick={() =>
                                                    handleSendReminder(
                                                        account.tenant.id,
                                                    )
                                                }
                                                disabled={
                                                    sendingReminder ===
                                                    account.tenant.id
                                                }
                                                style={{
                                                    opacity:
                                                        sendingReminder ===
                                                        account.tenant.id
                                                            ? 0.7
                                                            : 1,
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Box className={classes.emptyState}>
                        <Warning
                            style={{
                                fontSize: 48,
                                marginBottom: 16,
                                color: theme.palette.grey[400],
                            }}
                        />
                        <Typography variant="h6" style={{ marginBottom: 8 }}>
                            No Delinquent Accounts
                        </Typography>
                        <Typography variant="body2">
                            All accounts are current
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box className={classes.paginationContainer}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        variant="outlined"
                        shape="rounded"
                        disabled={totalPages <= 1}
                    />
                    {totalCount > 0 && (
                        <Box
                            style={{
                                marginLeft: 16,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                            }}
                        >
                            <Typography variant="body2" color="textSecondary">
                                Showing {startIndex + 1} to{' '}
                                {Math.min(endIndex, totalCount)} of {totalCount}{' '}
                                accounts
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    )
}
